const API_URL = import.meta.env.VITE_API_URL;

interface RequestOptions extends RequestInit {
  params?: Record<string, any>;
}

class ApiClient {
  private async request(endpoint: string, options: RequestOptions = {}) {
    const { params, headers, body, ...rest } = options;

    // Add query parameters to URL
    let url = `${API_URL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    // Get token from localStorage or sessionStorage
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

    // Default headers
    const defaultHeaders: Record<string, string> = {};
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Don't set Content-Type if body is FormData (browser will set it with boundary)
    if (!(body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...rest,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
    });

    // Handle 401 Unauthorized
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_user');
      // Potential improvement: trigger a global event or callback or just reload
      // window.location.href = '/login'; 
    }

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    // Auto-unwrap NestJS TransformInterceptor response
    let result = data;
    if (data && typeof data === 'object' && 'data' in data && 'statusCode' in data) {
      result = data.data;
    }

    // Filter test users and companies for all users
    const testEmails = [
      'oumar2.sy@gmail.com',
      'papadjibyniang@gmail.com',
      'syoumar505@gmail.com',
      'candidat@globaltravail.com',
      'ruwigybaf@mailinator.com'
    ].map(e => e.toLowerCase());

    const testCompanies = ['Oumar Dev tech'].map(c => c.toLowerCase());

    if (endpoint.startsWith('/users') && Array.isArray(result)) {
      result = result.filter((u: any) => !u.email || !testEmails.includes(u.email.toLowerCase()));
    } else if (endpoint.startsWith('/entreprises') && Array.isArray(result)) {
      result = result.filter((e: any) => !e.nom || !testCompanies.includes(e.nom.toLowerCase()));
    }

    return result;
  }

  get(endpoint: string, params?: Record<string, any>, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'GET', params });
  }

  post(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  put(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  patch(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  delete(endpoint: string, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient();
